import { FC, HTMLAttributes, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Achievement as AchievementType } from "@onmo/graphql/graphql";
import { Typography } from "../../core/Typography/Typography";
import {
  TypographyVariant,
  TypographyColor,
} from "../../../constants/typography";
import { StaticImage } from "../StaticImage";
import { DailyRewardHeader } from "../DailyRewardHeader";
import { NextTierCard } from "../NextTierCard";
import { convertAchievement } from "../../../models/achievement";
import { useStyles } from "./AchievementReward.style";

interface IAchievementRewardProps {
  newAchievement: AchievementType;
  oldAchievements: AchievementType[];
  nbCoin: number;
  onShowLevelUp: () => void;
}

export const AchievementReward: FC<
  HTMLAttributes<HTMLDivElement> & IAchievementRewardProps
> = (props) => {
  const classes = useStyles();
  const { nbCoin, newAchievement, oldAchievements, onShowLevelUp } = props;
  const { t } = useTranslation();
  const oldAchievement = useMemo(() => {
    return oldAchievements?.find(
      (a: AchievementType) => a.id === newAchievement.id,
    );
  }, [oldAchievements, newAchievement]);
  const oldFullAchievement = useMemo(() => {
    if (oldAchievement) {
      return convertAchievement(oldAchievement);
    }
  }, [oldAchievement]);
  const newFullAchievement = useMemo(() => {
    return convertAchievement(newAchievement);
  }, [newAchievement]);

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <Typography
          variant={TypographyVariant.HeadingH3Bold}
          className={classes.achievementName}
        >
          {t(newFullAchievement?.achievementName || "")}
        </Typography>
        <div className={classes.achieve}>
          <StaticImage
            src={oldFullAchievement?.achievementImage?.src1x}
            src2x={oldFullAchievement?.achievementImage?.src2x}
            src3x={oldFullAchievement?.achievementImage?.src3x}
            className={classes.currentAchieve}
          />
          <StaticImage
            src={newFullAchievement?.achievementImage?.src1x}
            src2x={newFullAchievement?.achievementImage?.src2x}
            src3x={newFullAchievement?.achievementImage?.src3x}
            className={classes.nextAchieve}
          />
        </div>
        <Typography
          variant={TypographyVariant.HeadingH5Bold}
          className={classes.tier}
        >
          {t("commom_tier", { amount: newFullAchievement?.currentTier })}
        </Typography>
        <Typography
          variant={TypographyVariant.BodyB4Medium}
          color={TypographyColor.Silver}
        >
          {t(newFullAchievement?.description || "", {
            amount: newFullAchievement.currentTierTarget,
            appName: import.meta.env.VITE_APP_NAME,
          })}
        </Typography>
        <DailyRewardHeader nbCoin={nbCoin} className={classes.coins} />
        <NextTierCard
          className={classes.nextTierCard}
          newAchievement={newAchievement}
        />
      </div>
      <Typography
        variant={TypographyVariant.HeadingH6Bold}
        className={classes.skip}
        onClick={onShowLevelUp}
      >
        {t("Tap anywhere to continue")}
      </Typography>
    </div>
  );
};
